import { Link } from "react-router-dom";
import { usePortfolioProjects } from "../hooks/usePortfolioSummary";
import { formatImpact } from "../../../utils/format";
import { Section } from "../../../components";
import { ProjectWithImpacts } from "../api/portfolio";
import { useRangeMode } from "../../impact/context/RangeModeContext";
import { isRangeReady, metricRatios, type ScenariosBlock } from "../../impact/domain/scenarios";

interface Props {
  filters?: Record<string, string>;
  collapsed?: boolean;
  onToggle?: () => void;
  scenarios?: ScenariosBlock;
  analysisMode?: "ctg" | "ops";
}

const TECH_LABELS: Record<string, string> = {
  solar_ground: "Solar (Ground)",
  solar_rooftop: "Solar (Rooftop)",
  wind_onshore: "Wind (Onshore)",
  wind_offshore: "Wind (Offshore)",
};

const PROFILE_LABELS: Record<string, string> = {
  sam_generated: "SAM",
  user_provided: "Uploaded",
};

/** Profile-source pill copy: 4-way split across metered/simulated x
 * operating/future. Existing + user_provided = metered settlement
 * data; existing + SAM = TotalView-estimated; future + user_provided
 * = customer-supplied forward sim; future + SAM = TotalView forward
 * estimate. See Plan Harris Phase A+ for the data-quality rationale. */
export function getProfileSourceLabel(
  source: string | null | undefined,
  existing: boolean | null | undefined,
): { label: string; tone: "metered" | "sam" | "customer" | "sam-future" } {
  if (!source) return { label: "—", tone: "sam" };
  if (existing) {
    return source === "user_provided"
      ? { label: "Metered", tone: "metered" }
      : { label: "Estimated (SAM)", tone: "sam" };
  }
  return source === "user_provided"
    ? { label: "Customer Provided (Sim)", tone: "customer" }
    : { label: "TotalView Estimated (SAM)", tone: "sam-future" };
}

const PILL_TONE: Record<string, string> = {
  metered: "bg-quantum-500/20 text-quantum-300 border-quantum-500/40",
  sam: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  customer: "bg-health-500/20 text-health-300 border-health-500/40",
  "sam-future": "bg-surface-500/40 text-text-muted border-border",
};

export function ProfileSourcePill({
  source,
  existing,
}: {
  source: string | null | undefined;
  existing: boolean | null | undefined;
}) {
  const { label, tone } = getProfileSourceLabel(source, existing);
  if (label === "—") return <span className="text-text-muted">{label}</span>;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium whitespace-nowrap ${PILL_TONE[tone]}`}
    >
      {label}
    </span>
  );
}

/** Compact coverage bar: analyzed years / operational years with a mini progress bar. */
function CoverageIndicator({ project }: { project: ProjectWithImpacts }) {
  if (!project.cod_date || !project.existing_project_flag) return <span>{"\u2014"}</span>;

  const codDate = new Date(project.cod_date);
  const now = new Date();
  const totalHours = Math.max(1, (now.getTime() - codDate.getTime()) / (1000 * 60 * 60));
  const analyzedHours = project.hours_modeled || 0;
  const pct = Math.min(100, (analyzedHours / totalHours) * 100);

  const totalYears = Math.max(1, totalHours / 8760);
  const analyzedYears = analyzedHours / 8760;

  const label = `${analyzedYears.toFixed(1)}/${totalYears.toFixed(0)}yr`;
  const source = project.profile_source
    ? PROFILE_LABELS[project.profile_source] || project.profile_source
    : "";

  return (
    <div className="flex flex-col items-end gap-0.5" title={`${analyzedHours.toLocaleString()} of ~${Math.round(totalHours).toLocaleString()} operational hours analyzed (${source})`}>
      <span className="text-body-sm text-text-secondary leading-none">
        {label} <span className="text-text-muted text-xs">{source}</span>
      </span>
      <div className="w-full h-1 rounded-full bg-surface-500 overflow-hidden">
        <div
          className={`h-full rounded-full ${pct > 80 ? "bg-quantum-400" : pct > 30 ? "bg-yellow-400" : "bg-health-400"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function PortfolioProjectList({ filters, collapsed, onToggle, scenarios, analysisMode = "ctg" }: Props) {
  const hasFilters = !!filters && Object.keys(filters).length > 0;
  const { mode: rangeMode } = useRangeMode();
  const { data: projects, isLoading } = usePortfolioProjects({
    filters: hasFilters ? filters : undefined,
    limit: 200,
  });

  // The overview carries fleet-level bands, not per-project ones; every
  // avoided quantity scales with realized displacement, so the fleet's
  // floor/ceiling ratios map each project's central DALYs onto its bracket.
  const ratios =
    rangeMode === "range" && isRangeReady(scenarios)
      ? metricRatios(scenarios, analysisMode === "ops" ? "ops" : "ctg", "dalys")
      : null;
  const showRange = !!ratios;
  const gridCols = showRange
    ? "grid-cols-[1fr_auto_auto_auto_auto_auto_auto_auto_auto_auto_auto]"
    : "grid-cols-[1fr_auto_auto_auto_auto_auto_auto_auto_auto_auto]";

  if (isLoading) {
    return (
      <Section>
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-12 rounded-lg bg-surface-700 animate-pulse" />
          ))}
        </div>
      </Section>
    );
  }

  if (!projects?.length) return null;

  return (
    <Section>
      <button
        onClick={onToggle}
        className="flex items-center gap-2 w-full text-left mb-1"
      >
        <svg
          className={`w-4 h-4 text-text-muted transition-transform ${collapsed ? "" : "rotate-180"}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        <h3 className="text-body-sm font-semibold text-text-primary">
          Projects ({projects.length})
        </h3>
      </button>

      {!collapsed && (
        <div className="rounded-xl bg-surface-700 border border-border overflow-hidden mt-2">
          {/* Header */}
          <div className={`grid ${gridCols} gap-x-4 items-center px-4 py-2 border-b border-border`}>
            <span className="text-caption text-text-muted uppercase tracking-wide">Name</span>
            <span className="text-caption text-text-muted uppercase tracking-wide text-right w-24">Tech</span>
            <span className="text-caption text-text-muted uppercase tracking-wide text-right w-16">MW</span>
            <span className="text-caption text-text-muted uppercase tracking-wide text-right w-20">COD</span>
            <span className="text-caption text-text-muted uppercase tracking-wide text-right w-16">CF</span>
            <span className="text-caption text-text-muted uppercase tracking-wide text-left w-40">Source</span>
            <span className="text-caption text-text-muted uppercase tracking-wide text-right w-28">Coverage</span>
            <span className="text-caption text-text-muted uppercase tracking-wide text-right w-20">DALYs</span>
            {showRange && (
              <span className="text-caption text-text-muted uppercase tracking-wide text-right w-28">Range</span>
            )}
            <span className="text-caption text-text-muted uppercase tracking-wide text-right w-20">PDFs</span>
            <span className="text-caption text-text-muted uppercase tracking-wide text-right w-24">USD</span>
          </div>
          {/* Rows */}
          {projects.map((p) => (
            <Link
              key={p.project_id}
              to={`/app/projects/${encodeURIComponent(p.project_id)}?name=${encodeURIComponent(p.project_name)}`}
              className={`grid ${gridCols} gap-x-4 items-center px-4 py-2.5 hover:bg-surface-600 transition-colors border-b border-border/50 last:border-b-0`}
            >
              <span className="text-body-sm text-text-primary truncate flex items-center gap-2">
                <span
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    p.latest_run_status === "completed"
                      ? "bg-quantum-400"
                      : p.latest_run_status === "running" || p.latest_run_status === "queued"
                      ? "bg-yellow-400"
                      : "bg-text-muted"
                  }`}
                />
                {p.project_name}
              </span>
              <span className="text-body-sm text-text-secondary text-right w-24 truncate">
                {TECH_LABELS[p.tech_type_id || ""] || p.tech_type_id || "\u2014"}
              </span>
              <span className="text-body-sm text-text-secondary text-right w-16">
                {Math.round(p.nameplate_capacity)}
              </span>
              <span className="text-body-sm text-text-secondary text-right w-20">
                {p.cod_date ? new Date(p.cod_date).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "\u2014"}
              </span>
              <span className="text-body-sm text-text-secondary text-right w-16">
                {p.capacity_factor != null ? `${(p.capacity_factor * 100).toFixed(1)}%` : "\u2014"}
              </span>
              <span className="text-left w-40">
                <ProfileSourcePill source={p.profile_source} existing={p.existing_project_flag} />
              </span>
              <span className="text-right w-28">
                <CoverageIndicator project={p} />
              </span>
              <span className={`text-body-sm text-right w-20 ${p.dalys != null && p.dalys < 0 ? "text-amber-400" : "text-health-400"}`}>
                {p.dalys != null ? formatImpact(p.dalys, "dalys") : "\u2014"}
              </span>
              {showRange && (
                <span className="text-caption text-text-muted text-right w-28 tabular-nums">
                  {p.dalys != null && ratios
                    ? `${formatImpact(p.dalys * ratios.floor, "dalys")}\u2013${formatImpact(p.dalys * ratios.ceiling, "dalys")}`
                    : "\u2014"}
                </span>
              )}
              <span className={`text-body-sm text-right w-20 ${p.pdfs != null && p.pdfs < 0 ? "text-amber-400" : "text-ecosystem-400"}`}>
                {p.pdfs != null ? formatImpact(p.pdfs, "pdfs") : "\u2014"}
              </span>
              <span className={`text-body-sm text-right w-24 ${p.usd != null && p.usd < 0 ? "text-amber-400" : "text-economic-400"}`}>
                {p.usd != null ? formatImpact(p.usd, "usd") : "\u2014"}
              </span>
            </Link>
          ))}
        </div>
      )}
    </Section>
  );
}
